import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAppConfig } from '../context/AppConfigContext';
import { useQuickLinks } from '../context/UserPreferencesContext';
import { NAV_GROUPS, isNavItemVisible, isQuickLinkable } from '../config/navigation';
import {
  FaUserCircle, FaSignOutAlt, FaTimes, FaThumbtack, FaPlus,
  FaAngleDoubleLeft, FaAngleDoubleRight
} from 'react-icons/fa';

const Sidebar = ({ collapsed, onToggleCollapse, mobileOpen, onCloseMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { config } = useAppConfig();
  const { isPinned, add, remove, atCapacity } = useQuickLinks();

  const visible = (items) => items.filter((item) => isNavItemVisible(item, config));

  const isActive = (item) => item.exact
    ? location.pathname === item.path
    : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const navItemClass = (active) =>
    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
      active ? 'bg-jims-cyan text-white font-semibold' : 'text-cyan-50 hover:bg-jims-cyan/60'
    }`;

  // Rows in the nav list split the styling in two: the wrapper paints the active/hover background so
  // the pin shares it, and the link only handles text. Never give the link a background class of its
  // own here — two same-specificity hover:bg-* rules tie, and the stylesheet's order decides which
  // wins, not the order they are written in.
  const navRowClass = (active) =>
    `group flex items-center rounded-md transition-colors ${active ? 'bg-jims-cyan' : 'hover:bg-jims-cyan/60'}`;

  const navRowLinkClass = (active) =>
    `flex flex-1 min-w-0 items-center gap-3 px-3 py-2 text-sm ${active ? 'text-white font-semibold' : 'text-cyan-50'}`;

  // Pin control per row. Hidden while collapsed (no room) and while the quick links are full, so the
  // only way to reach the cap is deliberate. A pinned page keeps its pin visible as the state badge.
  // Unpinned pins are hover-revealed on desktop only — there is no hover on a phone, so the mobile
  // drawer keeps them faintly visible instead of hiding the feature entirely.
  const renderPin = (item) => {
    if (collapsed || !isQuickLinkable(item.path)) return null;
    const pinned = isPinned(item.path);
    if (!pinned && atCapacity) return null;

    return (
      <button
        type="button"
        onClick={() => (pinned ? remove(item.path) : add(item.path))}
        title={pinned ? `Remove ${item.name} from dashboard quick links` : `Add ${item.name} to dashboard quick links`}
        aria-label={pinned ? `Remove ${item.name} from dashboard quick links` : `Add ${item.name} to dashboard quick links`}
        aria-pressed={pinned}
        className={`mr-1 shrink-0 p-1.5 rounded text-cyan-100 hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white
          transition-opacity ${pinned
            ? 'opacity-100'
            : 'opacity-60 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100'}`}
      >
        {pinned ? <FaThumbtack className="text-xs" /> : <FaPlus className="text-xs" />}
      </button>
    );
  };

  // The mobile drawer is always full width; only md+ honours the collapse toggle. The md: variant has to
  // be the sole companion to the base width — two unprefixed width classes tie and the cascade decides.
  return (
    <aside
      className={`bg-jims-cyan-dark text-white flex flex-col transition-all duration-200
        w-60 ${collapsed ? 'md:w-16' : 'md:w-60'}
        md:sticky md:top-0 md:h-screen md:translate-x-0 md:flex
        fixed inset-y-0 left-0 z-40 transform ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
    >
      <div className="flex items-center justify-between px-3 py-4">
        {!collapsed && <span className="font-bold text-lg">Loope Admin</span>}
        <button
          onClick={onToggleCollapse}
          className="hidden md:inline-flex items-center justify-center p-2 rounded hover:bg-jims-cyan/60"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />}
        </button>
        <button
          onClick={onCloseMobile}
          className="md:hidden inline-flex items-center justify-center p-2 rounded hover:bg-jims-cyan/60"
          aria-label="Close navigation"
        >
          <FaTimes />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        {NAV_GROUPS.map((group, idx) => {
          const items = visible(group.items);
          if (items.length === 0) return null;
          return (
            <div key={group.label || `group-${idx}`}>
              {group.label && !collapsed && (
                <div className="text-[11px] uppercase tracking-wider text-cyan-200 px-3 pt-4 pb-1">
                  {group.label}
                </div>
              )}
              {items.map((item) => {
                const active = isActive(item);
                return (
                  // The pin is a sibling of the link, never a child: a button inside an anchor is
                  // invalid markup and swallows the click. The row carries the active/hover tint.
                  <div key={item.path} className={navRowClass(active)}>
                    <Link
                      to={item.path}
                      className={navRowLinkClass(active)}
                      title={collapsed ? item.name : undefined}
                    >
                      <item.icon className="text-lg shrink-0" />
                      {!collapsed && <span className="truncate">{item.name}</span>}
                    </Link>
                    {renderPin(item)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="mt-auto border-t border-white/20 pt-3 px-2 pb-3">
        <Link to="/account" className={navItemClass(location.pathname === '/account')} title={collapsed ? 'Account' : undefined}>
          <FaUserCircle className="text-lg shrink-0" />
          {!collapsed && <span className="truncate">Account</span>}
        </Link>
        {!collapsed && (
          <div className="text-xs text-cyan-200 truncate px-3 py-1">
            {auth.currentUser?.email}
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors w-full text-red-100 hover:bg-red-600/40"
          title={collapsed ? 'Logout' : undefined}
        >
          <FaSignOutAlt className="text-lg shrink-0" />
          {!collapsed && <span className="truncate">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
